/* ===========================================================
   Conceito (/glossario/:slug) — apresentado como POST do blog (categoria
   "Conceitos"). Título-pergunta ("O que é X?"), layout editorial. O índice
   /glossario deixou de existir (redireciona para /blog). Conteúdo em
   content/glossario.ts. URL mantida semântica (/glossario/<slug>).
   =========================================================== */
import { Link, Navigate } from "react-router-dom";
import { useSeo } from "../../seo/use-seo";
import { titulo } from "../../seo/site";
import { JsonLd, breadcrumbLd, definedTermLd, articleLd } from "../../seo/jsonld";
import { acharVerbete, tempoLeituraSecoes } from "../../content/glossario";
import { useContentTracking } from "../../analytics/content-tracking";
import { ConteudoShell, Secoes, PostHeader, PostRelated, CtaDemo } from "./shell";

const HOME = { nome: "Início", path: "/" };
const BLOG = { nome: "Blog", path: "/blog" };
const DATA_BASE = "2026-06-26";

function linkVerbete(slug: string) {
  const v = acharVerbete(slug);
  return v ? { nome: v.termo, path: `/glossario/${v.slug}` } : null;
}

export function GlossarioTermo({ slug }: { slug: string | undefined }) {
  const v = acharVerbete(slug);
  // useSeo antes do early-return (regras de hooks). Em rota válida v existe sempre.
  useSeo({
    title: v ? titulo(v.tituloSeo) : titulo("Blog"),
    description: v ? v.resumo : "Conceitos de lucro e tráfego pago no e-commerce.",
    path: v ? `/glossario/${v.slug}` : "/blog",
    ogType: "article",
  });
  useContentTracking({ tipo: "conceito", categoria: "Conceitos", titulo: v?.termo });
  if (!v) return <Navigate to="/blog" replace />;
  const path = `/glossario/${v.slug}`;
  const relacionados = v.relacionados
    .map(linkVerbete)
    .filter((x): x is { nome: string; path: string } => x !== null);

  return (
    <ConteudoShell breadcrumb={[HOME, BLOG, { nome: v.pergunta, path }]}>
      <JsonLd data={breadcrumbLd([HOME, BLOG, { nome: v.pergunta, path }])} />
      <JsonLd data={definedTermLd({ termo: v.termo, definicao: v.resumo, path })} />
      <JsonLd
        data={articleLd({
          titulo: v.pergunta,
          descricao: v.resumo,
          path,
          publicadoEm: DATA_BASE,
          atualizadoEm: DATA_BASE,
        })}
      />
      <PostHeader
        categoria="Conceitos"
        titulo={v.pergunta}
        dek={v.resumo}
        publicadoEm={DATA_BASE}
        tempoMin={tempoLeituraSecoes(v.resumo, v.secoes)}
      />
      <div className="article-body">
        <Secoes secoes={v.secoes} />
      </div>
      {v.calculadora && (
        <p>
          <Link className="btn btn-soft btn-sm" to={v.calculadora.path}>
            {v.calculadora.rotulo} →
          </Link>
        </p>
      )}
      <PostRelated itens={relacionados} />
      <CtaDemo />
    </ConteudoShell>
  );
}
